/**
 * Human labels for a scope — the country, and the campaign inside it.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Stored scopes hold keys and ids, not names, so a scope can outlive the thing
 * it points at (a deleted campaign, a country the catalog no longer returns).
 * Every lookup falls back to the raw id rather than rendering blank — an
 * override you can still see is one you can still remove.
 */

import { useCallback, useMemo } from 'react';

import { useAllCampaigns } from '@/hooks';
import { useCountries } from '@/hooks/smswiser/core/useSMSWiser';
import { countryToFlagEmoji } from '@/utils/country-flag-emoji';

import { normScope, scopeTarget, type TagRuleScope } from '../model';
import { campaignRowScope } from './campaign-scope-options';

export interface ScopeLabel {
  /** Flag for the country, empty in the default view. */
  icon: string;
  /** The country name, or 'Default'. */
  country: string;
  /** Campaign name when one is selected. */
  campaign?: string;
  /** What a write would land on, in words: "France" or "CASINO SMS AU". */
  target: string;
  /** 'country' | 'campaign' | 'default' */
  level: ReturnType<typeof scopeTarget>;
}

export function useScopeLabels(): {
  labelFor: (scope: TagRuleScope) => ScopeLabel;
  isLoading: boolean;
} {
  const { data: countries = [], isLoading: loadingCountries } = useCountries();
  const { data: campaigns = [], isLoading: loadingCampaigns } = useAllCampaigns();

  const countryByKey = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of countries) m.set(normScope(c.key), c.name);
    return m;
  }, [countries]);

  const campaignByKey = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of campaigns ?? []) {
      const scope = campaignRowScope(c); // none for a tracker without a campaign tier
      if (scope) m.set(`${scope.provider}:${normScope(String(c.id))}`, c.name?.trim() || String(c.id));
    }
    return m;
  }, [campaigns]);

  const labelFor = useCallback(
    (scope: TagRuleScope): ScopeLabel => {
      const level = scopeTarget(scope);
      if (level === 'default') {
        return { icon: '', country: 'Default', target: 'the default', level };
      }
      // Look the NAME up first: countryToFlagEmoji matches on names, and the
      // key→ISO2 shortcut mangles multi-word countries.
      const key = normScope(scope.countryKey ?? '');
      const country = countryByKey.get(key) ?? scope.countryKey ?? '';
      const icon = countryToFlagEmoji(country);
      if (level === 'country') {
        return { icon, country, target: country, level };
      }
      const campKey = `${scope.provider}:${normScope(scope.campaignId ?? '')}`;
      const campaign = campaignByKey.get(campKey) ?? `Campaign ${scope.campaignId}`;
      return { icon, country, campaign, target: campaign, level };
    },
    [countryByKey, campaignByKey],
  );

  return { labelFor, isLoading: loadingCountries || loadingCampaigns };
}
